import { SETTING_DEFAULTS } from '@wiztime/core';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutMode, useLayoutMode } from '../../hooks/useMediaQuery';
import { useNow } from '../../hooks/useNow';
import { useStore } from '../../hooks/useServices';
import { useSettingsState } from '../../hooks/useSettings';
import { useVisibleBlocks } from '../../hooks/useVisibleBlocks';
import { CatchUpButton } from './cards/CatchUpButton';
import { CheckinCard } from './cards/CheckinCard';
import { DaySummaryCard } from './cards/DaySummaryCard';
import { FirstUseHint } from './cards/FirstUseHint';
import { NoteCard } from './cards/NoteCard';
import { QuickMarkBanner } from './cards/QuickMarkBanner';
import { QuickMarkCard } from './cards/QuickMarkCard';
import { SuggestionCard } from './cards/SuggestionCard';
import { useWhenIdle } from '../../hooks/useWhenIdle';
import { UpcomingCard } from '../importantDates/UpcomingCard';
import { DayColumn, useColumnOrder } from './columnOrder';
import { DayDataContext, type DayData } from './DayDataContext';
import styles from './DayView.module.css';
import { useDayUi } from './dayUiStore';
import { BlockContextMenuHost } from './menu/BlockContextMenuHost';
import { PickerHost } from './picker/PickerHost';
import { QUICK_MARK_LOCK_MS, QuickMarkMode } from './quickMark';
import { buildGroups, findConflicts, overlapColumnCap } from './timeline/laneModel';
import { Timeline } from './timeline/Timeline';
import { useElementWidth } from './timeline/useElementWidth';
import { useDayCommands } from './useDayCommands';
import { useDayKeyboard } from './useDayKeyboard';
import { useProjectsForChoosing } from './useRankedProjects';

/** Grid track of each column in the wide layout (the tokens are declared in tokens.css). */
const WIDE_COLUMN_WIDTH: Record<DayColumn, string> = {
  [DayColumn.Timeline]: 'minmax(var(--col-timeline-min), 1fr)',
  [DayColumn.Checkin]: 'var(--col-side)',
  [DayColumn.QuickMark]: 'var(--col-assist)',
};

/** The day view (ui-spec §3): timeline, side cards and assist cards of one personal date. */
export function DayView({ date }: { date: string }) {
  // Important dates are secondary: their reads start after the day's own data has had the first chance.
  const idle = useWhenIdle();
  const { t } = useTranslation();
  const store = useStore();
  const settings = useSettingsState();
  const projects = useProjectsForChoosing();
  const visible = useVisibleBlocks(date);
  const now = useNow(30_000);
  const layout = useLayoutMode();
  const commands = useDayCommands();
  const columnOrder = useColumnOrder();

  // Opening a date purges its expired drafts, then fills in its drafts (both idempotent);
  // leaving it closes any popover.
  useEffect(() => {
    store.purgeExpiredDrafts([date]).catch((error: unknown) => {
      console.warn('expired draft purge failed', { date, error });
    });
    store.generateDraftsForDate(date).catch((error: unknown) => {
      console.warn('draft generation failed', { date, error });
    });
    const ui = useDayUi.getState();
    // A route change is a silent Dismiss path (2026-10b §5.4): the picker's modified fields
    // are committed without the "saved" toast; a dirty create context stays open and warns.
    ui.closeOverlays({ silent: true });
    ui.select(null);
  }, [store, date]);

  // The side-by-side overlap columns depend on the lane's measured width (ui-redesign §3.1.4).
  const laneRef = useRef<HTMLDivElement>(null);
  const laneWidthPx = useElementWidth(laneRef);
  const maxColumns = overlapColumnCap(laneWidthPx);
  const conflicts = useMemo(() => findConflicts(visible.blocks, maxColumns), [visible.blocks, maxColumns]);
  const groups = useMemo(() => buildGroups(visible.blocks, conflicts.ids), [visible.blocks, conflicts.ids]);
  const retrySettings = settings.retry;
  const retryProjects = projects.retry;
  const retryBlocks = visible.retry;
  const data = useMemo<DayData>(
    () => ({
      date,
      settings: settings.data ?? SETTING_DEFAULTS,
      projects,
      blocks: visible.blocks,
      blocksLoading: visible.loading || settings.loading || projects.loading,
      blocksError: visible.error ?? settings.error ?? projects.error,
      retryBlocks: () => {
        retryBlocks();
        retrySettings();
        retryProjects();
      },
      groups,
      conflicts,
      now,
    }),
    [
      date,
      settings.data,
      settings.loading,
      settings.error,
      projects,
      visible,
      groups,
      conflicts,
      now,
      retryBlocks,
      retrySettings,
      retryProjects,
    ],
  );
  useDayKeyboard(data, commands);

  // Phone "more" fold (2026-10b §4.7): the fold reacts only to quick-mark state changes.
  // A tile activation entering (or re-entering) Once keeps it open for the 500 ms double-click
  // window — the second tap that locks must still land on the tile — then collapses it;
  // entering Locked (second tap or long press) collapses it at once. Opening the fold while
  // the state is unchanged never re-arms the collapse: in quick-mark mode the tiles and the
  // suggestion card must stay reachable through the fold.
  const [moreOpen, setMoreOpen] = useState(false);
  const quickMark = useDayUi((state) => state.quickMark);
  const previousQuickMark = useRef(quickMark);
  const foldTimer = useRef<number | null>(null);
  useEffect(() => {
    const previous = previousQuickMark.current;
    previousQuickMark.current = quickMark;
    if (layout !== LayoutMode.Narrow || quickMark === previous) return;
    const collapseAfter = (delayMs: number): void => {
      if (foldTimer.current !== null) window.clearTimeout(foldTimer.current);
      foldTimer.current = window.setTimeout(() => {
        foldTimer.current = null;
        setMoreOpen(false);
      }, delayMs);
    };
    // A tile activation entered Once: (re)start the double-click window, then collapse.
    if (quickMark.mode === QuickMarkMode.Once) collapseAfter(QUICK_MARK_LOCK_MS);
    // Entering Locked collapses at once (next tick; a direct setState in an effect is barred).
    else if (quickMark.mode === QuickMarkMode.Locked) collapseAfter(0);
    // A transition to Off leaves a running window timer alone: the fold still collapses when
    // the window of the tile activation that armed it ends.
  }, [layout, quickMark]);
  useEffect(
    () => () => {
      if (foldTimer.current !== null) window.clearTimeout(foldTimer.current);
    },
    [],
  );

  const sideCards = (
    <>
      <NoteCard key={date} date={date} />
      <CheckinCard />
      <DaySummaryCard />
    </>
  );
  const assistCards = (
    <>
      <QuickMarkCard projects={projects} />
      <SuggestionCard />
      {idle && <UpcomingCard />}
    </>
  );
  // The two-column card stack (2026-10b §7.2): check-ins first, then quick mark, suggestions,
  // summary and note, so quick mark stays on the first screen.
  const stackCards = (
    <>
      <CheckinCard />
      {assistCards}
      <DaySummaryCard />
      <NoteCard key={date} date={date} />
    </>
  );
  const timeline = (
    <section className={styles.colTimeline} data-testid="col-timeline" aria-label={t('timeline.title')}>
      <Timeline laneRef={laneRef} />
    </section>
  );

  const timelineColumn = (
    <div className={styles.timelineWrap}>
      <div className={styles.timelineTop}>
        <FirstUseHint />
        <QuickMarkBanner />
      </div>
      {timeline}
    </div>
  );

  return (
    <DayDataContext.Provider value={data}>
      {layout === LayoutMode.Narrow ? (
        <div className={styles.day} data-layout={layout} data-testid="day-view">
          <div className={styles.timelineTop}>
            <FirstUseHint />
            <QuickMarkBanner />
            <CatchUpButton />
          </div>
          {timeline}
          <CheckinCard />
          <details
            className={styles.more}
            data-testid="more-section"
            open={moreOpen}
            onToggle={(event) => setMoreOpen(event.currentTarget.open)}
          >
            <summary>{t('more.title')}</summary>
            <div className={styles.moreBody}>
              {assistCards}
              <DaySummaryCard />
              <NoteCard key={date} date={date} />
            </div>
          </details>
        </div>
      ) : layout === LayoutMode.Medium ? (
        // Two columns (2026-10b §7.2, 768-1101px): the elastic timeline, then one card
        // stack ordered check-ins, quick mark, suggestions, summary, note.
        <div className={styles.dayRoot}>
          <div className={styles.day} data-layout={layout} data-testid="day-view">
            {timelineColumn}
            <div className={`${styles.column} ${styles.colRight}`} data-testid="col-right">
              {stackCards}
            </div>
          </div>
        </div>
      ) : (
        // Three columns (2026-10b §7.1): the timeline (elastic main column),
        // the check-in column (check-ins, summary, note) and the quick-mark column (quick mark +
        // suggestions), left to right in the order chosen on this device.
        <div className={styles.dayRoot}>
          <div
            className={styles.day}
            style={{ gridTemplateColumns: columnOrder.map((column) => WIDE_COLUMN_WIDTH[column]).join(' ') }}
            data-layout={layout}
            data-column-order={columnOrder.join(',')}
            data-testid="day-view"
          >
            {columnOrder.map((column) => {
              if (column === DayColumn.Timeline) return <Fragment key={column}>{timelineColumn}</Fragment>;
              if (column === DayColumn.Checkin) {
                return (
                  <div key={column} className={`${styles.column} ${styles.colSide}`} data-testid="col-side">
                    {sideCards}
                  </div>
                );
              }
              return (
                <div key={column} className={`${styles.column} ${styles.colAssist}`} data-testid="col-assist">
                  {assistCards}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <PickerHost />
      <BlockContextMenuHost />
    </DayDataContext.Provider>
  );
}
